import { action, makeObservable, observable } from 'mobx';

import { TableRowEntity } from 'entities/TableRow';
import { ILocalStore } from 'stores/interfaces';

type PrivateFields = '_rows' | '_rowOrder';

/**
 * Store for raw row entity data without creating controllers.
 * Used for virtualization - stores all row data but only creates
 * controllers for visible rows.
 */
class TableRowDataStore implements ILocalStore {
  constructor() {
    makeObservable<TableRowDataStore, PrivateFields>(this, {
      _rows: observable.ref,
      _rowOrder: observable.ref,
      setRows: action.bound,
      updateRow: action.bound,
      clear: action.bound,
    });
  }

  private _rows: Map<string, TableRowEntity> = new Map();
  private _rowOrder: string[] = [];

  get rowIds(): string[] {
    return this._rowOrder;
  }

  get rowCount(): number {
    return this._rowOrder.length;
  }

  getRowEntity(rowId: string): TableRowEntity | null {
    return this._rows.get(rowId) ?? null;
  }

  setRows(rows: TableRowEntity[]): void {
    this._rows = new Map();
    this._rowOrder = [];

    rows.forEach((row) => {
      this._rows.set(row.rowId, row);
      this._rowOrder.push(row.rowId);
    });
  }

  updateRow(rowId: string, entity: TableRowEntity): void {
    const newMap = new Map(this._rows);
    newMap.set(rowId, entity);
    this._rows = newMap;
  }

  clear(): void {
    this._rows = new Map();
    this._rowOrder = [];
  }

  destroy(): void {
    this.clear();
  }
}

export default TableRowDataStore;
